import { a as CART_CLEAR_EVENT, C as CART_UPDATE_EVENT, w as ONLINE_STORE_CHECKOUT_TYPE, S as STOREFRONT_API_TEST_FAILED } from './built-constants-439d5572.js';
import { c as cartEventEmitter } from './built-cart-event-emitter-def8079a.js';
import './built-env-utils-ed419642.js';
import { s as scriptsConfig, D as DEBUG } from './built-config-a65ae98c.js';
import { l as log } from './built-loglevel-42c63e3e.js';
import { p as pcConfig, n as nanographql, _ as _taggedTemplateLiteralLoose, a as makeUncachedRequest } from './built-make-request-bc649a18.js';
import { C as Cookies } from './built-js.cookie-7baadf9c.js';
import { t as testStorefrontAPI, b as customCheckoutInit } from './built-index-60e8946f.js';
import { b as removeItemFromLocalStorage, c as getObjectFromLocalStorage, d as setObjectInLocalStorage, e as getStorefrontAPITested, f as setStorefrontAPITestedState, l as localStorageAvailable, h as cookiesAvailable, a as sessionStorageAvailable } from './built-storage-042e9783.js';
import { w as wasFailedCheckout, g as getCheckingOutFlagTime, a as getCheckingOutFlagURL, u as unsetCheckingOutFlag, o as onCheckout, s as setCheckingOutFlag, t as trackCheckout } from './built-checkout-helpers-05d82694.js';
import { u as updateActualCart } from './built-cart-quantity-checker-3fa4acf7.js';

// @ts-check
const {
  LOGGED_IN,
  SHOPIFY_CART
} = pcConfig.STORAGE;
/**
 * Get a saved Shopify cart from localStorage
 * @returns {Cart} cart - The JSON.parsed cart object from localStorage
 */

const getStoredShopifyCart = () => getObjectFromLocalStorage(SHOPIFY_CART);
/**
 * Sets a Shopify cart object in localStorage
 * @param {Cart} cart - The cart object to be stored
 */

const setStoredShopifyCart = cart => setObjectInLocalStorage(SHOPIFY_CART, cart);
/**
 * Removes a Shopify cart object in localStorage
 */

const removeStoredShopifyCart = () => removeItemFromLocalStorage(SHOPIFY_CART);
/**
 * Sets a key in localStorage flagging that the customer was logged in as of this times
 */

const setWasLoggedIn = () => setObjectInLocalStorage(LOGGED_IN, true);
/**
 * Gets a boolean in localStorage flagging the state of customer login
 * Returns false if the key is not set
 * @returns {boolean} isLoggedIn
 */

const getWasLoggedIn = () => Boolean(getObjectFromLocalStorage(LOGGED_IN));
/**
 * Removes a boolean in localStorage flagging the state of customer login
 */

const removeWasLoggedIn = () => removeItemFromLocalStorage(LOGGED_IN);
/**
 * A plain object for saving off some values used in our main chain of async functions,
 * across functions
 */

const cache = {
  customerID: null,
  customer: null,

  /**
   * Stores the cart returned by Shopify after setting the `cart` cookie stored in the DB
   * @type {Cart | null}
   */
  masterShopifyCart: null,
  customerCartExpired: false,
  currentCartCount: 0,
  setNewCustomerOrCart: false
};

// @ts-check
/**
 * 1. Check to see if the Storefront API has been tested (health check), and the
 *    result persisted (it is set to eventually expire and eventually be
 *    re-tested)
 * 2. If there is no persisted test result, query the API and persist the result
 * 3. Return the result
 * @returns {Promise<boolean>} Storefront API has been tested recently
 * and is working
 */

const persistedStorefrontAPITest = async () => {
  let storefrontAPIWorks = getStorefrontAPITested();

  if (storefrontAPIWorks === null) {
    storefrontAPIWorks = await testStorefrontAPI();
    setStorefrontAPITestedState(storefrontAPIWorks);
  }

  return storefrontAPIWorks;
};

function _templateObject2() {
  const data = _taggedTemplateLiteralLoose(["\n  query($customerID: ID!) {\n    getCustomer(customerID: $customerID) {\n      customerID\n      cart\n      cartID\n    }\n  }\n"]);

  _templateObject2 = function _templateObject2() {
    return data;
  };

  return data;
}

function _templateObject() {
  const data = _taggedTemplateLiteralLoose(["\n  mutation($customerID: ID!, $cart: JSON) {\n    createOrUpdateCustomer(customerID: $customerID, cart: $cart) {\n      customerID\n      cart\n      cartID\n    }\n  }\n"]);

  _templateObject = function _templateObject() {
    return data;
  };

  return data;
}
const {
  API_URL
} = pcConfig;
/**
 * Request to create or update a customer in the DB
 * @param {Object} params
 * @param {*} params.mutation - A GQL mutation for the PC application to create or update a customer with a cart
 * @param {string} params.customerID - A GQL ID
 * @param {Cart} params.cart - A Shopify cart
 * @returns {Promise<{cart: Cart, cartID: string, customerID: string}>} - A customer (with cart) from our DB
 */

const createOrUpdateCustomerRequest = ({
  mutation,
  customerID,
  cart
}) => makeUncachedRequest(mutation, {
  customerID,
  cart
}, API_URL).then(data => data.createOrUpdateCustomer).catch(error => {
  throw new Error("Error creating or updating customer in database: " + error.message);
});
/**
 * Takes a customerID and queries our DB for a customer
 * @param {Object} params
 * @param {*} params.query - A GQL query for sending data to the PC GQL endpoint to get a customer from the DB
 * @param {string} params.customerID - A customer ID string
 * @returns {Promise<{cart: Cart, cartID: string, customerID: string} | null>} - A customer from the DB (or null if there is no customer with this ID)
 */


const getCustomerRequest = ({
  query,
  customerID
}) => makeUncachedRequest(query, {
  customerID
}, API_URL).then(data => data.getCustomer).catch(error => {
  throw new Error("Error getting customer from database: " + error.message);
});
/**
 * A GQL mutation for creating or updating a customer with a cart object
 */


const createOrUpdateCustomerMutation = nanographql(_templateObject());
/**
 * A GQL query for getting a customer by customerID from the DB
 */

const getCustomerQuery = nanographql(_templateObject2());
/**
 * Wraps the request, using the default query for getting a full customer from the GQL interface
 * @param {string} customerID
 * @returns customer - From the database
 */

const getCustomer = customerID => getCustomerRequest({
  query: getCustomerQuery,
  customerID
});
/**
 * Wraps the request to a GQL mutation passing in the mutation and data for the request
 * @param {Object} params
 * @param {string} params.customerID
 * @param {Cart} params.cart
 */


const createOrUpdateCustomer = ({
  customerID,
  cart
}) => createOrUpdateCustomerRequest({
  mutation: createOrUpdateCustomerMutation,
  customerID,
  cart
});

// @ts-check

/**
 * @typedef {Object} LineItem - Item variant and quantity objects, formatted for
 * using in a Shopify Ajax API query
 * @property {number} id - A variant ID
 * @property {number} quantity - A quantity for a variant (line item) in a cart
 */

/**
 * @typedef {{[variant_id: string]: number}} CartPayload
 */

/**
 * Get items from a Shopify cart or the cart in the DB
 * Note that the cart returned by a webhook does not match the
 * structure of a cart returned by the AJAX API
 * @param {CartItem[]} items - Items extracted from a cart
 * @returns {LineItem[]} - Query-ready line-item objects
 */
const getLineItems = items => items.map(({
  variant_id: id,
  quantity
}) => ({
  id,
  quantity
}));
/**
 * Get items from a cart
 * @param {Cart} cart - A Shopify cart
 * @returns {CartItem[] | []}
 */


const getItemsFromCart = cart => cart && (cart.items || cart.line_items) || []; // @TODO - implement and remove comments

/**
 * Merges two carts to produce a merged object of line-item details (variant_id
 * and quantity)
 * This array should be used to update an existing Shopify cart in a single request
 * @see https://help.shopify.com/en/themes/development/getting-started/using-ajax-api#update-cart
 * @param {Cart|null} cart1 - One cart to reconcile
 * @param {Cart|null} cart2 - Another cart to reconcile
 * @returns {CartPayload} mergedCart - Object of line_item key/value
 * pairs, where the key is a variant_id (id property) the value is a quantity.
 * If the variant_id of two line items is identical, the quantity will be
 * accumulated to a single line_item
 */


const cartReconciler = (cart1, cart2) => {
  /**
   * Get the line items for both carts (Our DB and from Shopify),
   * as an array of objects with ids an quantities
   */
  const combinedCart = [...getLineItems(getItemsFromCart(cart1)), ...getLineItems(getItemsFromCart(cart2))];
  /**
   * Merge Shopify and DB cart line_item objects to a single array,
   * combining quantities for identical line_items
   */

  return combinedCart.reduce((acc, curr) => {
    // Does the current item's id exist on the accumulated object?
    const matching = acc[curr.id]; // If so, add to quantity for the existing ID, else add this id to the
    // add the current quantity to a matching item or to 0 if it doesn't exist

    acc[curr.id] = (matching || 0) + curr.quantity;
    return acc;
  }, {});
};

const {
  CART_COOKIE,
  ALL_CART_COOKIES
} = pcConfig;
/**
 * Sets the cart cookie
 * @param {string} cartId The cookie to set
 */

const setCartCookie = cartId => Cookies.set(CART_COOKIE, cartId);
/**
 * Get the cart cookie value
 * @returns {string|undefined}
 */

const getCartCookie = () => Cookies.get(CART_COOKIE);
/**
 * Remove the cart cookie
 */

const removeCartCookie = () => Cookies.remove(CART_COOKIE);

/**
 * lodash (Custom Build) <https://lodash.com/>
 * Build: `lodash modularize exports="npm" -o ./`
 * Copyright jQuery Foundation and other contributors <https://jquery.org/>
 * Released under MIT license <https://lodash.com/license>
 * Based on Underscore.js 1.8.3 <http://underscorejs.org/LICENSE>
 * Copyright Jeremy Ashkenas, DocumentCloud and Investigative Reporters & Editors
 */

/** `Object#toString` result references. */
var objectTag = '[object Object]';
/**
 * Checks if `value` is a host object in IE < 9.
 *
 * @private
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a host object, else `false`.
 */

function isHostObject(value) {
  // Many host objects are `Object` objects that can coerce to strings
  // despite having improperly defined `toString` methods.
  var result = false;

  if (value != null && typeof value.toString != 'function') {
    try {
      result = !!(value + '');
    } catch (e) {}
  }

  return result;
}
/**
 * Creates a unary function that invokes `func` with its argument transformed.
 *
 * @private
 * @param {Function} func The function to wrap.
 * @param {Function} transform The argument transform.
 * @returns {Function} Returns the new function.
 */


function overArg(func, transform) {
  return function (arg) {
    return func(transform(arg));
  };
}
/** Used for built-in method references. */


var funcProto = Function.prototype,
    objectProto = Object.prototype;
/** Used to resolve the decompiled source of functions. */

var funcToString = funcProto.toString;
/** Used to check objects for own properties. */

var hasOwnProperty = objectProto.hasOwnProperty;
/** Used to infer the `Object` constructor. */

var objectCtorString = funcToString.call(Object);
/**
 * Used to resolve the
 * [`toStringTag`](http://ecma-international.org/ecma-262/7.0/#sec-object.prototype.tostring)
 * of values.
 */

var objectToString = objectProto.toString;
/** Built-in value references. */

var getPrototype = overArg(Object.getPrototypeOf, Object);
/**
 * Checks if `value` is object-like. A value is object-like if it's not `null`
 * and has a `typeof` result of "object".
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is object-like, else `false`.
 * @example
 *
 * _.isObjectLike({});
 * // => true
 *
 * _.isObjectLike([1, 2, 3]);
 * // => true
 *
 * _.isObjectLike(_.noop);
 * // => false
 *
 * _.isObjectLike(null);
 * // => false
 */

function isObjectLike(value) {
  return !!value && typeof value == 'object';
}
/**
 * Checks if `value` is a plain object, that is, an object created by the
 * `Object` constructor or one with a `[[Prototype]]` of `null`.
 *
 * @static
 * @memberOf _
 * @since 0.8.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a plain object, else `false`.
 * @example
 *
 * function Foo() {
 *   this.a = 1;
 * }
 *
 * _.isPlainObject(new Foo);
 * // => false
 *
 * _.isPlainObject([1, 2, 3]);
 * // => false
 *
 * _.isPlainObject({ 'x': 0, 'y': 0 });
 * // => true
 *
 * _.isPlainObject(Object.create(null));
 * // => true
 */


function isPlainObject(value) {
  if (!isObjectLike(value) || objectToString.call(value) != objectTag || isHostObject(value)) {
    return false;
  }

  var proto = getPrototype(value);

  if (proto === null) {
    return true;
  }

  var Ctor = hasOwnProperty.call(proto, 'constructor') && proto.constructor;
  return typeof Ctor == 'function' && Ctor instanceof Ctor && funcToString.call(Ctor) == objectCtorString;
}

var lodash_isplainobject = isPlainObject;

/**
 * Sets up decorators or listeners on the AJAX Add-To-Cart functionality
 * of the Shopify store. If an add-to-cart event is detected, this
 * will call the initialization of the Persistent Cart client,
 * which will then determine whether to add a customer to the persistent-cart app
 * database, with the newly updated cart.
 *
 * This is needed for cases when a new customer logs in with an empty cart,
 * adds one or more items to their cart, and never reloads the page during
 * that session.
 *
 * @see /assets/ajax-cart.js(.liquid) for reference
 * @param {function(number)} persistentCartInit - An initialization function (callback) from
 * the main JS.
 * Takes the line_item quantity from an item successfully added to the cart.
 * This callback is called after an item is added to the cart using AJAX (no page reload).
 */

const onCartAjaxUpdated = persistentCartInit => {
  // Check for the ShopifyAPI object, and abort if it does not exist
  const ShopifyAPI = window.ShopifyAPI;

  if (!ShopifyAPI || !ShopifyAPI.addItemFromForm) {
    return;
  }
  /**
   * Decorates function arguments to Shopify.addItemFromForm
   * Finds which argument to those functions is a Shopify line_item
   * This value is obtained during the triggering of the callback (function),
   * which is passed to the "success" property of the jQuery AJAX call used
   * by the Shopify.addItemFromForm method - aka, the "add-to-cart" AJAX success event
   *
   * @param {function} fn - A function that is an argument to ShopifyAPI.addItemFromForm
   * @returns {function} - The decorated function argument to Shopify.addItemFromForm.
   * Success adding an item can now be captured used to trigger the Persistent Cart,
   * to add a customer to the database without a page reload.
   */


  const argDecorator = fn => (...args) => {
    const RESPONSE_TEXT = 'responseText'; // Detect if an error was returned in this Ajax ATC request

    const isError = args.find(arg => typeof arg === 'string' && arg.includes('error'));

    if (isError) {
      // Capture the error, if it can be obtained and has a `responseText` property
      const errorObj = args.find(arg => lodash_isplainobject(arg) && RESPONSE_TEXT in arg); // ... And log it

      if (errorObj) {
        console.error('Error adding item to 🛒 cart', errorObj[RESPONSE_TEXT]);
      }
    } // Check that this argument is a product by checking both variant_id and quantity properties


    const lineItemObject = args.find( // Ensure that arg is a JS object before using prop in object checks
    arg => lodash_isplainobject(arg) && 'variant_id' in arg && 'quantity' in arg);

    if (lineItemObject) {
      // Initialize the application, passing in the quantity of the item just added to the cart
      persistentCartInit(lineItemObject.quantity);
    }

    return fn(...args);
  };
  /**
   * Map over the arguments of the Shopify.addItemFromForm method.
   * For arguments that are functions, pass to another decorator
   * which detects which argument of that function is a product that
   * was added to the cart via AJAX. This is the signal that an
   * item was added to the cart.
   * @param {...*} args - The original arguments passed to ShopifyAPI.addItemFromForm
   * @returns {function} - The decorated addItemFromForm method
   */


  const originalShopifyAddItemFromForm = ShopifyAPI.addItemFromForm;

  ShopifyAPI.addItemFromForm = (...args) => {
    const newArgs = args.map(arg => typeof arg === 'function' ? argDecorator(arg) : arg);
    return originalShopifyAddItemFromForm(...newArgs);
  };
};

const {
  SELECTORS: {
    LOGOUT
  }
} = scriptsConfig;
/**
 * LOGOUT HANDLING
 * Clear the cart cookie on logout
 */

const logoutHandler = () => {
  removeCartCookie();
};

const logoutHandlerInit = () => {
  const logoutLink = document.querySelector(LOGOUT);

  if (logoutLink) {
    logoutLink.addEventListener('click', logoutHandler);
  }
};

/**
 * Checks whether the value passed in has a property message,
 * returns either the original value or the value of the `.message`
 * property, if it exists
 * @param {Object|string} error - An error object or string
 * @returns {string} An error messsage
 */
const getErrorMessage = error => typeof error.message === 'string' ? error.message : error;

// @ts-check

/**
 * Takes an array of parameters, each of which should be passed into a
 * Promise-returning function. The functions will be run sequentially, after
 * the previous one has resolved. Any rejection will short-circuit the chain.
 * @template InputType
 * @template OutputType
 * @param {InputType[]} array - An array of parameters, any kind, to pass
 * into the Promise-returning function defined in the 2nd parameter
 * @param {function(InputType):Promise<OutputType>} fn - Any Promise-returning function
 * @returns {Promise<OutputType[]>} - A Promise for the array of the results from the
 * chained Promise calls
 */
const chainPromises = (array, fn) => array.reduce((p, item) => p.then(prevArray => fn(item).then(data => prevArray.concat(data))), Promise.resolve([]));

// @ts-check
const {
  GET_CART,
  UPDATE_CART,
  ADD_TO_CART
} = pcConfig.SHOPIFY_API;
const {
  CART_COUNT
} = scriptsConfig.SELECTORS;
const shopifyRequestOptions = {
  headers: {
    'Content-Type': 'application/json',
    pragma: 'no-cache',
    'cache-control': 'no-cache'
  },
  credentials:
  /** @type {RequestCredentials} */
  'same-origin'
};
const shopifyPostOptions = Object.assign({}, shopifyRequestOptions, {
  method: 'POST'
});
/**
 * Update the UI and storage, return the cart
 * @param {Cart} cart - A Shopify cart object
 * @returns {Cart} cart - the same cart
 */

const finalCartUpdates = cart => {
  if (!cart) {
    throw new Error('Cart not passed to handler for updating UI.');
  } // @TODO - Is this necessary. Do we actually want this to be ephemeral and go away?


  setStoredShopifyCart(cart); // Update the UI with the new count

  cartEventEmitter.emit(CART_UPDATE_EVENT, cart);
  return cart;
};
/**
 * Update a customer in the DB with a rehydrated cart from Shopify
 * Should only be called in case of cartID/token expiration
 * At this point, our master cartID was nullified, and webhooks
 * need to look for the customer with this new cart's token / cartID
 * @param {Cart} cart - The (locally stored) Shopify cart object
 * @returns {Promise<Cart>} The cart belonging to the returned, updated customer object
 */


const updateDBCustomer = (cart = getStoredShopifyCart()) => {
  // Need to update the customer in the DB only if we had a Shopify expiration event
  return createOrUpdateCustomer({
    customerID: cache.customer.customerID,
    cart
  }).then(updatedCustomer => updatedCustomer.cart);
};
/**
 * Handle errors thrown by calls to the Shopify AJAX API for cart endpoints
 * @param {Response} shopifyResponse - Response from a call to a Shopify
 * AJAX API cart endpoint
 * @returns {Promise<Object>} - Dependent on the calling function
 */


const parseShopifyCartResponse = async shopifyResponse => {
  const {
    ok,
    statusText,
    status
  } = shopifyResponse;

  if (!ok || status > 300) {
    throw new Error("Error from Shopify Ajax API, updating or adding to \uD83D\uDED2 cart.\n      Response status: " + status + ".\n      " + (statusText ? "Response statusText: " + statusText : ''));
  }

  const responseObj = await shopifyResponse.json();

  if (ok) {
    return responseObj;
  }

  if (responseObj.description && responseObj.description.toLowerCase() === 'cannot find variant') {
    throw new Error('A product in the 🛒 cart is invalid');
  }

  throw new Error("Attempt to update or rebuild \uD83D\uDED2 cart failed with error: " + responseObj.message);
};
/**
 * Add an item to a Shopify cart using the Ajax API
 * Never rejects or throws, just returns `null` if the item cannot be added
 * to the cart (suspected to be the result of trying to add a now-invalid
 * product variant)
 * @param {Object} item
 * @param {string} item.id
 * @param {string} item.quantity
 * @returns {Promise<CartItem|null>} - A product variant object (result of a
 * successful addition) or null
 */


const addToCart = ({
  id,
  quantity
}) => {
  const updateOptions = Object.assign({}, shopifyPostOptions, {
    body: JSON.stringify({
      id,
      quantity
    })
  });
  /**
   * Call fetch with the above config.
   * Return `null` for any failed 'add' - Likely cause is an invalid product
   * variant, and it will just not get re-added to the Shopify cart
   */

  return fetch(ADD_TO_CART, updateOptions).then(parseShopifyCartResponse).catch(error => {
    console.error("Error adding an item to a cart from request body " + updateOptions.body + ":", error);
    return null;
  });
};
/**
 * Attempt to rebuild the Shopify cart item-by-item by using the `/cart/add.js`
 * endpoint (if a call to `/cart/update.js` has failed)
 * @param {Object} reconciledCarts
 * @returns {Promise<Cart>} - A Shopify cart
 */


const rebuildCart = reconciledCarts => {
  const items = Object.keys(reconciledCarts);
  return chainPromises(items.map(item => ({
    id: item,
    quantity: reconciledCarts[item]
  })), addToCart).then(validatedItems => {
    return fetchShopifyCart();
  });
};
/**
 * Call to update the Shopify cart using the `/cart/update.js` endpoint
 * @param {import('./cart-reconciler').CartPayload} reconciledCarts
 * @returns {Promise<Cart|null>} - A Shopify cart
 */


const updateCart = reconciledCarts => {
  const updateOptions = Object.assign({}, shopifyPostOptions, {
    body: JSON.stringify({
      updates: reconciledCarts
    })
  });

  return fetch(UPDATE_CART, updateOptions).then(parseShopifyCartResponse);
};
/**
 * Use a reconciled cart object as payload to update the Shopify cart
 * This is considered "rehydrating" if the previously used cartID/token was
 * found to be expired by Shopify
 * @param {import('./cart-reconciler').CartPayload} reconciledCarts - An object
 * of line_items used to update the cart
 * @returns {Promise<Cart>} - The updated Shopify cart
 */


const modifyShopifyCart = (reconciledCarts = null) => {
  if (!reconciledCarts) {
    throw new Error('modifyShopifyCart was called without a payload.');
  }
  const newCartID = cache.customerCartExpired ? cache.masterShopifyCart.token : cache.customer.cartID;

  if (!newCartID) {
    throw new Error("Before updating the Shopify cart, there is no token to set.");
  }
  /**
   * If the `cache.customerCartExpired` boolean is `true`, update the cart cookie
   */


  if (cache.customerCartExpired) {
    setCartCookie(newCartID);
  }
  /**
   * Now call "/cart/update.js" (a POST request)
   * to the Shopify API to update the cart in their DB
   */


  return updateCart(reconciledCarts).catch(error => {
    console.error('/cart/update.js returned with error', error);
    /**
     * If an error was thrown attempting to use `/cart/update.js`, now try
     * to rebuild the cart using sequential `/cart/add.js` requests
     */

    return rebuildCart(reconciledCarts);
  }).then(updatedCart => {
    if (updatedCart && 'item_count' in updatedCart) {

      setStoredShopifyCart(updatedCart);
      return updatedCart;
    }

    throw new Error("No cart returned after attempting to update or rebuild using token " + newCartID + (reconciledCarts ? ", with reconciled carts: " + reconciledCarts : '') + ".");
  });
};
/**
 * Reconcile two carts, if needed
 * @returns The merged variant quantities object
 */


const reconcileCarts = () => {
  /**
   * If we're here, we already know that the existing cart from page load
   * has a different token value than the customer's cartID in the DB
   * We need to reconcile: 1) The cart from page load, and 2) if we've detected
   * that Shopify has expired an existing cart, the DB customer.cart, otherwise
   * the master Shopify cart
   */
  return cartReconciler(
  /**
   * NOTE - Previously, the logic for the first parameter, in the event
   * of a cart expiration event, was to set it to `null`. This resulted in
   * existing items in cart being thrown away instead of reconciled.
   * Changing to always pass in the cart from page load: `getStoredShopifyCart()`
   */
  getStoredShopifyCart(),
  /**
   * NB - This is the only place in which cart contents from the DB can be used
   * for updating the cart, due to a Shopify expiration event
   */
  cache.customerCartExpired ? cache.customer.cart : cache.masterShopifyCart);
};
/**
 * Check whether carts need reconciliation (Shopify and customer's DB cart ID
 * do not match)
 * Carts should be reconciled if 1) There were items in the cart on load,
 * 2) the cart ID on load doesn't match the customer's cartID in the DB, and
 * 3) the user wasn't logged in on last load (reconciliation only needs to
 * happen directly after login), or
 * 4) Shopify has expired our existing cart from the DB
 * @returns {boolean} - Should reconcileCarts be run?
 */


const shouldReconcileCarts = () => {
  const existingCustomer = cache.customer;
  const shopifyCart = getStoredShopifyCart();
  /*
   * Return true if we've detected that Shopify has expired our cart
   */

  if (cache.customerCartExpired) {
    return true;
  }
  /**
   * Return false if there were no items in the cart
   * or the customer was already logged in on previous page load
   */


  if (!shopifyCart || getWasLoggedIn()) {
    return false;
  }
  /**
   * Errors if there is no existing customer in the DB at this point or no
   * `shopifyCart.token`
   */


  if (!existingCustomer || !shopifyCart.token) {
    throw new Error("Checking whether carts should be reconciled failed because\n      " + cache.customerID + " or a Shopify cart ID could not be found.");
  } // Check existingCustomer.cartID against shopifyCart token value


  const cartIDsMismatch = (existingCustomer == null ? void 0 : existingCustomer.cartID) && existingCustomer.cartID !== shopifyCart.token;
  return cartIDsMismatch;
};
/**
 * Call out to create a new customer with cart if the cart has items (was set) and isn't already in our DB
 * @param {Cart} [shopifyCart] - A locally stored Shopify cart object
 * @returns {boolean | Promise<boolean>} - Whether or not to continue on with the Promise chain for other PC tasks
 */


const assignCartIfNew = (shopifyCart = getStoredShopifyCart()) => {
  var _existingCustomer;

  let existingCustomer = cache.customer;
  const setNewCustomerOrCart = shopifyCart && (!existingCustomer || existingCustomer && !existingCustomer.cartID);
  cache.setNewCustomerOrCart = setNewCustomerOrCart;

  if (setNewCustomerOrCart) {
    return createOrUpdateCustomer({
      customerID: cache.customerID,
      cart: shopifyCart
    }).then(customer => {
      // Update the in-memory store of the new or updated customer
      cache.customer = customer;
      existingCustomer = customer;
      return false;
    });
  }
  /**
   * At this point, we know that we didn't create or update a customer
   * If a customer exists and has a cartID, continue
   */


  if ((_existingCustomer = existingCustomer) == null ? void 0 : _existingCustomer.cartID) {

    return true;
  }
  /**
   * There's nothing more to do if the cart is empty and there isn't already a customer in the DB
   * or there is a customer with no cart
   * There may be a customer with no cart if a customer is in our DB without a cart
   * and this is a page load event with 0 items in the cart (we do not use an empty cart to update or create
   * a customer in the DB - in the event that the Shopify `cart.token` changes again)
   */


  if (!shopifyCart && !existingCustomer || existingCustomer && !existingCustomer.cartID) {
    return false;
  } // If none of the above conditions passes, there's a problem


  throw new Error("Customer " + cache.customerID + " should already have been set in the database, but has not been.");
};
/**
 * Fetch a Shopify cart, with or without first setting the cartID (i.e., the "page-load" vs the "master" carts)
 * @param {string} [cartID] - A cart ID (aka, token) to be used to set the cart cookie
 * before making the GET request
 * @returns {Promise<Cart>} A Shopify cart
 */


const fetchShopifyCart = cartID => {
  if (cartID) {
    setCartCookie(cartID);
  }

  return fetch(GET_CART, shopifyRequestOptions).then(res => res.json()).then(cart => {
    if (!cart) {
      throw new Error("Could not fetch a cart from Shopify with cartID " + (cartID || getCartCookie()) + ".");
    }

    return cart;
  });
};
/**
 * Get a Shopify cart on page load from the Storefront AJAX API
 * Called without first setting a cart cookie, unlike the "master" cart
 * @returns {Promise<Cart|null>} - A Shopify cart or null if there is nothing in the cart on page load
 */


const fetchPageLoadShopifyCart = () => cache.currentCartCount > 0 ? fetchShopifyCart() : Promise.resolve(null);
/**
 * Save off a Shopify cart locally, for reference
 * @param {Cart} [cart] - The Shopify cart object
 * @returns {Cart|null} cart - A cart if one was stored, null if one was not stored
 */


const persistPageLoadShopifyCart = cart => {
  // Persist the Shopify cart locally, for the moment, then return it
  // If a cart is not passed, will remove stored cart from localStorage, otherwise set it
  if (cart) {
    setStoredShopifyCart(cart);
  } else {
    removeStoredShopifyCart();
  }

  return getStoredShopifyCart();
};
/**
 * Save the boolean flagging whether a customer's cart was expired by Shopify
 * @param {boolean} expired - Whether or not Shopify has expired the cart we've saved in the DB
 * @returns {boolean} Returns the `expired` argument
 */


const persistCustomerCartExpired = expired => {
  cache.customerCartExpired = expired;
  return expired;
};
/**
 * Check whether Shopify has expired the cart associated with the cartID associated with
 * a customer record in the database
 * @param {Cart} [masterShopifyCart] - The cart retrieved from Shopify after setting with the customer.cartID from the DB
 * @returns {boolean} -  Whether or not Shopify has expired the cart (should rarely be true, usually false)
 */


const isCustomerCartExpired = (masterShopifyCart = cache.masterShopifyCart) => masterShopifyCart && masterShopifyCart.token !== cache.customer.cartID;
/**
 * Get a "master" Shopify cart by passing in the customer.cartID from the database
 * This cartID will be used to set the cart cookie before GETting a cart from the AJAX API
 * Referring to this cart as the "master"
 * @returns {Promise<Cart>} - A Shopify cart
 */


const fetchMasterShopifyCart = () => {
  /**
   * Pass the cart cookie to make sure we're getting the master cart.
   * `fetchShopifyCart` will use the passed cartID to set the cart cookie
   * before making a GET request to the Shopify AJAX API
   */
  return fetchShopifyCart(cache.customer.cartID);
};
/**
 * Persist the "master" Shopify cart, retrieved by first setting the cart cookie
 * to the value of the customer.cartID from the DB
 * @param {Cart} cart - The cart object returned from Shopify
 * @returns {Cart} The same cart object
 */


const persistMasterShopifyCart = cart => {
  cache.masterShopifyCart = cart;
  return cart;
};
/**
 * Check whether the token of the cart fetched on page load matches the customer's cartID
 * No need to overwrite the cookie or many other things in this flow if they match
 * @returns {boolean} - Should we fetch the "master" cart?
 */


const shouldMasterCartBeFetched = () => {
  var _cache$customer;

  const pageLoadShopifyCart = getStoredShopifyCart();
  const pageLoadToken = (pageLoadShopifyCart == null ? void 0 : pageLoadShopifyCart.token) ? pageLoadShopifyCart.token : null;
  const customerCartID = ((_cache$customer = cache.customer) == null ? void 0 : _cache$customer.cartID) ? cache.customer.cartID : null;

  if (pageLoadToken === customerCartID) {
    return false;
  }

  return true;
};
/**
 * Assign a customer object DB to a global customer object for further reference, return
 * @params {Object|null} retrievedCustomer - The value of a customer retrieved from the custom DB
 * @returns {Object|null} - The same value
 */


const persistCustomer = retrievedCustomer => {
  // Persist the customer locally, for the moment (could be null)
  cache.customer = retrievedCustomer;
  return retrievedCustomer;
};
/**
 * The end of the PC Promise chain, called if it's determined that carts need
 * reconciling and Shopify and/or the DB, and the UI, need updating
 * @param {import('./cart-reconciler').CartPayload} reconciledCarts - The object used to call to the Shopify AJAX API to update a cart
 * @returns {Promise<Cart>} - The Shopify cart after the PC flow has finished completely
 */


const handleAllNecessaryUpdates = reconciledCarts => modifyShopifyCart(reconciledCarts).then(cart => {
  return cache.customerCartExpired ? updateDBCustomer(cart) : cart;
}).then(finalCartUpdates);
/**
 * Determine whether to fetch a master Shopify cart (using the customer's cartID)
 * If needed, fetch and persist, otherwise, return the cart from page load
 * @returns {Promise<Cart>} - A Shopify cart
 */


const getCartForUpdates = () => shouldMasterCartBeFetched() ? fetchMasterShopifyCart().then(persistMasterShopifyCart) : Promise.resolve(getStoredShopifyCart());
/**
 * Kick off a new portion of the Promise chain by getting the "master" Shopify
 * cart (which will use the customer's cartID from the DB as the cart cookie value)
 * Will check and persist the master cart and a boolean flagging whether Shopify has expired
 * the existing customer.cartID. Finally, will determine whether to reconcile carts
 * @returns - A call to continue cart reconciliation and update tasks or to end by storing the master
 * cart and update the UI with its item_count
 */


const handleReconciliationAndExpiration = () => {
  let cartToUseForUpdates;
  return getCartForUpdates().then(
  /** @type {Cart} */
  cart => {

    cartToUseForUpdates = cart;
    return isCustomerCartExpired(cart);
  }).then(persistCustomerCartExpired).then(shouldReconcileCarts).then(shouldReconcile => {
    if (shouldReconcile) {
      const reconciledCarts = reconcileCarts();
      return handleAllNecessaryUpdates(reconciledCarts);
    }

    return finalCartUpdates(cartToUseForUpdates);
  });
};
/**
 * Call a chain of functions to create or update a customer in our DB,
 * get and locally save items from a Shopify cart (if needed), and update
 * Shopify (if needed)
 * @param {Customer} customer - A customer retrieved from our DB
 */


const pcInit = customer => {
  persistCustomer(customer);
  return fetchPageLoadShopifyCart().then(persistPageLoadShopifyCart).then(assignCartIfNew).then(shouldHandleReconciliationAndExpiration => // @ts-ignore
  shouldHandleReconciliationAndExpiration ? handleReconciliationAndExpiration() : "" + (cache.setNewCustomerOrCart ? "A customer was just created or updated: ' " + cache.customer.customerID : "There's no saved customer, or the saved customer has no cart, and the Shopify cart is empty, so ending"));
};
/**
 * If there is a customerID in the template on page load, an attempt is made to fetch
 * the customer from the DB, which will return a customer Object or null (no such customer in the DB).
 * - Kicks off PC with this value. Also binds the Ajax Handler for add-to-cart actions.
 * - Runs a Promise chain for enabling persistent cart, catches errors.
 * - Sets a flag to indicate the customer was logged in at the end of running.
 * @param {Customer|null} customer - The customer value retrieved from the application DB (or null if no customer was yet in the DB)
 */


const initWithCustomer = customer => {

  logoutHandlerInit(); // Initialize the persistent cart flow

  pcInit(customer).then(response => {
    if (response == null ? void 0 : response.error) {
      throw new Error(getErrorMessage(response.error));
    }
    /**
     * Set a flag showing that the user was logged in as of this time.
     * Do this only in the "finally" block of when all of the PC flow is complete
     * This flag will be unset as soon as the page loads without a customerID
     */


    setWasLoggedIn();
  }) // Catch all errors from ensuing calls
  .catch(error => {
    // @TODO - Promise.finally might need shimming. Instead, just call setLoggedIn on both success and failure
    setWasLoggedIn();
    console.error('Persistent cart errors or messages: ', getErrorMessage(error));
  }); // Pass PC client initialization as a callback to the add-to-cart module

  onCartAjaxUpdated(quantity => {
    /**
     * First update the cart quantity to add to what was in the template at page load
     */
    cache.currentCartCount += quantity; // Now call the application init function

    pcInit(customer);
  });
};
/**
 * If there is no customerID in the template on page load, run cleanup
 * - If and only if the user transitioned from a logged-in state:
 * - Clears cart cookies
 * - Updates the UI with cart item count 0
 * Also
 * - Removes logged in flag that was set the last time the page loaded
 * with a customer ID in the template
 * - Removes any saved Shopify cart in storage
 */


const initWithoutCustomer = () => {
  /**
   * If a flag was previously set that the user was logged in and now they are not
   * remove the cart cookie, and also update the cart item count to 0.
   * Cart cookie reset is also bound to click on the logout button, but testing
   * indicated that a fallback might be necessary.
   */
  if (getWasLoggedIn()) {
    removeCartCookie();
    cartEventEmitter.emit(CART_CLEAR_EVENT);
  }
  /**
   * Set a flag showing that the user was not logged in as of this time.
   * Do this only when there is no customerID in the template
   * This flag will be set after page loads without a customerID and
   * when the PC flow is complete
   */


  removeWasLoggedIn();
  /**
   * Remove any locally saved cart information
   */

  removeStoredShopifyCart();
};
/**
 * Kick off persistent cart, called on DOM Content Loaded
 * Will check for the following, and not proceed if conditions aren't met
 * 1) A customer exists
 * 2) A query for the customer can be made to the DB successfully - a health check of the PC app
 * 2) localStorage is supported and usable - TBD whether this is necessary
 * 3) Cookies are enabled/usable
 * Catches and logs for the chain of request-making functions it calls.
 * @param {string} customerID
 */

const pcCheckInit = customerID => {
  /** @type {HTMLInputElement | null} */
  const cartCountEl = document.querySelector(CART_COUNT);
  /**
   * The value of currentCartCount may be "0" unless there are items in the
   * cart at the time of login. PC un-sets the cart cookie on logout, and it
   * will not be set again unless 1 or more items are added to the cart
   */

  cache.currentCartCount = parseInt(cartCountEl.value, 10);

  cache.customerID = customerID; // Test for a customer plus localStorage and cookie support (enabled), or do not proceed

  if (localStorageAvailable && cookiesAvailable) {
    if (cache.customerID) {
      getCustomer(cache.customerID).then(customerResponse => {
        /**
         * Assuming
         * - The Storefront API was successfully queried and
         * - Either there's no customer in our DB yet or there is and Mongo
         *   did not return an error
         * Proceed
         */

        if (!customerResponse || typeof customerResponse === 'object' && !('error' in customerResponse)) {
          initWithCustomer(customerResponse);
          return;
        }

        if ('error' in customerResponse) {
          // @ts-ignore
          throw new Error(customerResponse.error);
        }
      }).catch(error => {
        console.error('Persistent cart cannot be initialized', error);
        removeStoredShopifyCart();
      });
    } else {
      initWithoutCustomer();
    }
  }
};

// @ts-check
const {
  CUSTOMER_ID
} = scriptsConfig.SELECTORS;
/**
 * Cleanup actions to take on initialization failures, depending on whether or
 * not a customer is logged in
 * @param {string} customerID
 */

const initCleanup = customerID => {
  if (customerID) {
    removeStoredShopifyCart();
  } else {
    initWithoutCustomer();
  }
};
/**
 * Add a listener to the `window.unload` event after submitting the checkout form
 * The callback will then:
 * 1. Set a flag indicating that navigation to checkout is happenning
 * 2. Log that this user is going to an online-store (not custom) checkout
 * @param {string} url - the URL to log
 */


const addOnlineStoreCheckoutListener = url => {
  window.addEventListener('unload', () => {
    setCheckingOutFlag(url);
    trackCheckout({
      checkoutType: ONLINE_STORE_CHECKOUT_TYPE,
      message: STOREFRONT_API_TEST_FAILED + ". Submitting checkout form with action " + url + "."
    });
  });
};
/**
 * Initialize on DOM Content Loaded:
 * - Run a health-check request to the Shopify Storefront API, and attempt to
 *   persist this value to storage for future checks (with an expiration).
 *   Conditionally runs cleanup if `sessionStorage` is not available
 * - If that succeeds:
 *     - Initialize custom checkouts (auth and guest users, so
 *       with or without PC running)
 *     - Kick off the PC check and initialize flow (which will run PC for auth
 *       users, only, given other criteria are met)
 * - If the query to the Storefront API fails
 *     - Do not initialize custom checkouts
 *     - Run a cleanup initialization for PC (same operations as happen when a
 *       user logs out)
 */


document.addEventListener('DOMContentLoaded', async () => {
  // Get cid from template

  /** @type {HTMLInputElement} */
  const cidEl = document.querySelector(CUSTOMER_ID);
  const customerID = (cidEl == null ? void 0 : cidEl.value) || null; //mise a jour des quantites dans le cart pour utilisation ulterieure

  updateActualCart();

  if (sessionStorageAvailable) {
    // Check the (possibly cached) value of a Storefront API test
    const storefrontAPIWorks = await persistedStorefrontAPITest();
    /**
     * Detect a failed checkout flag on load
     */

    const failedCheckoutFlag = wasFailedCheckout();

    if (failedCheckoutFlag && DEBUG) {
      const time = new Date().getTime();
      const flagTime = getCheckingOutFlagTime();
      const url = getCheckingOutFlagURL();
      const sinceSet = (Number(time) - Number(flagTime)) / 1000;
      log.debug("Failed checkout flagged at " + time + ". " + sinceSet + " seconds since the flag\n        was last set at " + flagTime + " with checkout URL " + url + ".");
      /**
       * Avoid unnecessarily re-reporting failed checkouts, e.g. if the user just
       * refreshes the page
       */

      unsetCheckingOutFlag();
    }

    if (storefrontAPIWorks) {
      /**
       * A checkout flag will be set within custom checkout flow if we've gotten
       * to the point where the Storefront API passes health checks
       * @see `scripts/shared/custom-checkout/index.js`
       */
      // Initialize custom checkout for all customers: Favro DEC-3130
      {
        customCheckoutInit();
      }

      pcCheckInit(customerID);
    } else {
      /**
       * The `onCheckout` helper wraps a callback that will set a `checking-out`
       * flag and log the event when the user is about to transition to checkout
       */
      onCheckout(
      /** @this {HTMLFormElement} - The cart form */
      function () {
        const urlToLog = this.action;
        addOnlineStoreCheckoutListener(urlToLog);
      });
      initCleanup(customerID);
    }
  } else {
    console.error('Session storage test failed, cannot persist Storefront API health check results');
    initCleanup(customerID);
  }
});
